import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Download, Loader2, ShoppingBag } from "lucide-react";

export default function Purchases() {
  const { t } = useI18n();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (ready && user === false) navigate("/login");
  }, [ready, user, navigate]);

  useEffect(() => {
    if (user) api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  const download = async (o) => {
    try {
      const res = await api.get(`/downloads/${o.product_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = o.product?.title || "download"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Download error"); }
  };

  if (!orders) return <div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0EA5E9]" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 py-10">
      <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-8">{t("purchases.title")}</h1>
      {orders.length === 0 ? (
        <div className="text-center py-24" data-testid="purchases-empty">
          <div className="h-16 w-16 rounded-2xl bg-sky-50 text-[#0EA5E9] flex items-center justify-center mx-auto mb-5"><ShoppingBag className="h-8 w-8" /></div>
          <p className="text-[#475569] mb-6">{t("purchases.empty")}</p>
          <Link to="/explore"><Button className="rounded-full bg-[#0F172A] text-white">{t("purchases.explore")}</Button></Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="purchases-list">
          {orders.map((o) => (
            <div key={o.id} className="flex items-center gap-4 bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid={`purchase-${o.product_id}`}>
              <img src={fileUrl(o.product?.preview_image_url)} alt="" className="h-16 w-16 rounded-xl object-cover bg-slate-100" />
              <div className="flex-1 min-w-0">
                <Link to={`/product/${o.product_id}`} className="font-display font-bold text-[#0F172A] hover:text-[#0EA5E9] truncate block">{o.product?.title || o.product_title}</Link>
                <div className="text-sm text-[#94A3B8]">${Number(o.amount).toFixed(2)} · {new Date(o.created_at).toLocaleDateString()}</div>
              </div>
              <Button onClick={() => download(o)} data-testid={`download-order-${o.product_id}`}
                className="rounded-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold">
                <Download className="h-4 w-4 mr-1" /> {t("product.download")}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
